/**
 * Add/Edit Vehicle side drawer. Launched from the opportunity form's
 * "+ Add Vehicle" dropdown (Buy/Sell/Trade/Service). For a Buy it shows two tabs:
 *   - Inventory      -> pick an existing inventory vehicle
 *   - Vehicle Request -> VIN decode + manual entry + preferences + other details
 * Sell/Trade/Service have no inventory step, so the tab bar is dropped and the
 * request form is shown on its own. Edit mode also hides the Inventory tab.
 *
 * The drawer owns tab/selection/VIN state and validation; on save it assembles a
 * {@link Vehicle} item and hands it to `onSave` — the parent merges it into
 * `state.vehiclesOfInterest`. It never talks to the save mutation directly.
 *
 * @module vehicleModal/VehicleFormModal
 */
import { Button, Drawer, Form, Tabs, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { firstUpperCaseWordParser } from "../../../library/utils";
import CustomerPreferencesSection from "./components/CustomerPreferencesSection";
import VehicleConditionSection from "./components/VehicleConditionSection";
import InventoryVehicleSelector from "./components/InventoryVehicleSelector";
import OtherDetailsSection from "./components/OtherDetailsSection";
import VehicleDetailsForm from "./components/VehicleDetailsForm";
import VinLookupSection from "./components/VinLookupSection";
import { SELL_SIDE_INTERESTS, TAB_KEYS, VIN_LENGTH } from "./constants";
import useVehicleForm from "./hooks/useVehicleForm";
import useVinLookup from "./hooks/useVinLookup";
import { formValuesToVehicle, inventoryToVehicle } from "./utils/vehiclePayload";
import { validateVehicle } from "./utils/vehicleValidation";

/** Decoded VIN fields that should auto-fill the form on a successful lookup. */
const VIN_AUTOFILL_FIELDS = [
  "year",
  "make",
  "model",
  "trim",
  "bodyStyle",
  "fuelType",
  "transmission",
  "driveType",
  "vehicleType",
  "engineCylinders",
  "doors",
  "seats",
  "driveTrain",
];

/**
 * Every vehicle field except `vin`. When the VIN is removed or a new complete
 * VIN is entered the form describes a different vehicle, so these are wiped so
 * stale manual/decoded values don't linger (decode then re-fills the subset it
 * knows about).
 */
const RESETTABLE_FIELDS = [
  // condition / certified / primary
  "condition",
  "isCertified",
  "isPrimary",
  // identity & specs
  "year",
  "make",
  "model",
  "trim",
  "exteriorColor",
  "interiorColor",
  "bodyStyle",
  "fuelType",
  "transmission",
  "stockNumber",
  "modelNumber",
  "driveType",
  "vehicleType",
  "engineCylinders",
  "doors",
  "seats",
  "driveTrain",
  "msrp",
  // customer preferences (buy)
  "priceRangeMin",
  "priceRangeMax",
  "mileageRangeMin",
  "mileageRangeMax",
  // customer preferences (sell/trade)
  "odometer",
  "titleType",
  "expectedPrice",
  "payoffAmount",
  "mileage",
  // customer preferences (service)
  "plate",
  "unitNumber",
  "factoryWarrantyMonths",
  "factoryWarrantyMiles",
  "extendedWarrantyMonths",
  "extendedWarrantyMiles",
  "additionalNotes",
];

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {"ADD"|"EDIT"} props.mode
 * @param {import("./types").CustomerInterest} props.customerInterest
 * @param {import("./types").Vehicle|null} [props.initialVehicle] - edit source
 * @param {(vehicle: import("./types").Vehicle) => void} props.onSave
 * @param {() => void} props.onClose
 */
export default function VehicleFormModal({
  open,
  mode = "ADD",
  customerInterest,
  initialVehicle = null,
  onSave,
  onClose,
}) {
  // Edit always opens on the request form (we don't retain the full inventory
  // row); add defaults to Inventory like the screenshots.
  const [activeTab, setActiveTab] = useState(TAB_KEYS.INVENTORY);
  // Multiple inventory vehicles can be selected at once (checkbox table).
  const [inventorySelection, setInventorySelection] = useState([]);
  const [saving, setSaving] = useState(false);

  const { form, getValues } = useVehicleForm({ open, initialVehicle });
  const { status: vinStatus, error: vinError, lookup, reset: resetVin } = useVinLookup();

  // The Inventory picker only applies to a purchase. Sell/Trade/Service have no
  // inventory step, so the tab bar is dropped entirely and the request form is
  // shown on its own.
  const hideTabs = SELL_SIDE_INTERESTS.includes(customerInterest);

  // Reset transient state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setActiveTab(mode === "EDIT" || hideTabs ? TAB_KEYS.REQUEST : TAB_KEYS.INVENTORY);
    setInventorySelection([]);
    resetVin();
  }, [open, mode, hideTabs, resetVin]);

  const title = `${mode === "EDIT" ? "Edit" : "Add"} vehicle${
    customerInterest ? ` — ${firstUpperCaseWordParser(customerInterest)}` : ""
  }`;

  /** VIN-driven autofill: trigger lookup at 17 chars; reset below that. */
  const handleValuesChange = changed => {
    if (!("vin" in changed)) return;
    const vin = (changed.vin || "").trim();
    // Removing the VIN or entering a new complete VIN means a different vehicle:
    // wipe every other field first so previously entered/decoded data is gone.
    // (setFieldsValue does not re-fire onValuesChange, so this won't recurse.)
    if (vin.length === 0 || vin.length === VIN_LENGTH) {
      const cleared = {};
      RESETTABLE_FIELDS.forEach(field => {
        cleared[field] = undefined;
      });
      form.setFieldsValue(cleared);
    }
    if (vin.length === VIN_LENGTH) {
      lookup(vin).then(decoded => {
        if (!decoded) return; // notFound/error -> keep VIN, manual entry enabled
        const patch = {};
        VIN_AUTOFILL_FIELDS.forEach(field => {
          if (decoded[field] !== undefined) patch[field] = decoded[field];
        });
        form.setFieldsValue(patch);
      });
    } else {
      resetVin();
    }
  };

  // Run onSave; close the drawer only if it didn't explicitly fail (false).
  // onSave may persist to the backend, so it can be async.
  const commit = async payload => {
    setSaving(true);
    try {
      const result = await onSave(payload);
      if (result !== false) onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!hideTabs && activeTab === TAB_KEYS.INVENTORY) {
      const check = validateVehicle({
        tab: "inventory",
        inventorySelection: inventorySelection.length ? inventorySelection : null,
      });
      if (!check.valid) {
        message.error(check.errors[0]);
        return;
      }
      // Add every checked inventory vehicle as its own opportunity vehicle.
      await commit(inventorySelection.map(row => inventoryToVehicle(row, customerInterest)));
      return;
    }

    // Vehicle Request tab — run antd field rules first, then the cross-field gate.
    try {
      await form.validateFields();
    } catch (e) {
      return; // antd shows inline messages
    }
    const values = getValues();
    const check = validateVehicle({ tab: "request", values, vinStatus });
    if (!check.valid) {
      message.error(check.errors[0]);
      return;
    }
    await commit(
      formValuesToVehicle(values, {
        customerInterest,
        inputType: values.vin ? "vin" : "ymm",
        existing: initialVehicle || {},
      })
    );
  };

  const requestForm = (
    <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
      <VehicleConditionSection form={form} customerInterest={customerInterest} />
      <VinLookupSection status={vinStatus} error={vinError} />
      <VehicleDetailsForm form={form} requireCore={vinStatus !== "success"} />
      <CustomerPreferencesSection customerInterest={customerInterest} />
      <OtherDetailsSection />
    </Form>
  );

  const tabItems = useMemo(
    () => [
      // Inventory is an "add from inventory" picker; it has no meaning when
      // editing an existing vehicle, so hide it in EDIT mode.
      ...(mode === "EDIT"
        ? []
        : [
            {
              key: TAB_KEYS.INVENTORY,
              label: "Inventory",
              children: (
                <InventoryVehicleSelector
                  active={open && activeTab === TAB_KEYS.INVENTORY}
                  selectedIds={inventorySelection.map(v => v.id)}
                  onSelect={setInventorySelection}
                />
              ),
            },
          ]),
      {
        key: TAB_KEYS.REQUEST,
        label: "Vehicle Request",
        children: requestForm,
      },
    ],
    // form / handlers are stable enough; re-render on the values that affect UI
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, activeTab, inventorySelection, vinStatus, vinError, customerInterest, mode]
  );

  return (
    <Drawer
      className="normal-popup"
      title={title}
      open={open}
      onClose={onClose}
      width="60%"
      destroyOnClose
      footer={
        <div className="flex flex-row items-center gap-2">
          <Button type="primary" onClick={handleSave} loading={saving}>
            {mode === "EDIT" ? "Save Vehicle" : "Add Vehicle"}
          </Button>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      }
    >
      {hideTabs ? (
        requestForm
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      )}
    </Drawer>
  );
}
